import { memo } from 'react';
import { Stack, Box, Text, TextInput, Menu, Button, Group, Badge, Slider } from '@mantine/core';
import { IconRobot, IconWand, IconChevronDown, IconDeviceGamepad, IconGhost } from '@tabler/icons-react';
import { motion } from 'framer-motion';

/**
 * IdentityStep component for Step 1 of the bot setup wizard.
 * Handles bot naming and bot count configuration.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.form - Current form state.
 * @param {Function} props.setForm - Function to update form state.
 * @param {Object} props.errors - Current form errors.
 * @param {Function} props.setErrors - Function to update form errors.
 * @param {Function} props.handleKeyDown - Key down event handler.
 * @param {Function} props.generateBotName - Function to generate a random bot name.
 * @param {Object} props.variants - Motion variants for animation.
 */
const IdentityStep = memo(({
    form, setForm, errors, setErrors, handleKeyDown, generateBotName, variants
}) => {
    return (
        <motion.div
            key="step1"
            variants={variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ width: '100%', position: 'absolute' }}
        >
            <Stack>
                <Box>
                    <Text size="sm" fw={500} mb={5}>Bot Name</Text>
                    <TextInput
                        placeholder="John Doe"
                        autoFocus
                        leftSection={<IconRobot size={16} />}
                        rightSectionWidth={158}
                        rightSection={
                            <Menu shadow="md" width={140} position="bottom-end" withArrow>
                                <Menu.Target>
                                    <Button
                                        variant="light"
                                        color="blue"
                                        size="xs"
                                        radius="sm"
                                        leftSection={<IconWand size={12}/>}
                                        rightSection={<IconChevronDown size={12}/>}
                                    >
                                        Generate a name
                                    </Button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <Menu.Label>Pick a Style</Menu.Label>
                                    <Menu.Item
                                        leftSection={<IconDeviceGamepad size={14}/>}
                                        onClick={() => generateBotName('funny')}
                                    >
                                        Funny
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<IconGhost size={14}/>}
                                        onClick={() => generateBotName('stealth')}
                                    >
                                        Stealth
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<IconWand size={14}/>}
                                        onClick={() => generateBotName('glitch')}
                                    >
                                        Glitch
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        }
                        value={form.name}
                        onChange={(e) => {
                            setForm({ ...form, name: e.target.value });
                            setErrors({ ...errors, name: null });
                        }}
                        onKeyDown={(e) => handleKeyDown(e, 'next')}
                        error={errors.name}
                        required
                    />
                    {form.name.length > 0 && (
                        <Text size="xs" c="dimmed" mt={5}>
                            Preview: <b>{form.name}-01</b> ... through ... <b>{form.name}-{form.amount}</b>
                        </Text>
                    )}
                </Box>

                <Box mt="sm">
                    <Group justify="space-between" mb={5}>
                        <Text size="sm" fw={500}>Bot Count</Text>
                        <Badge size="sm" variant="light" color="blue">{form.amount} Bots</Badge>
                    </Group>
                    <Slider
                        value={form.amount}
                        onChange={(val) => setForm({ ...form, amount: val })}
                        min={1}
                        max={50}
                        step={1}
                        size="lg"
                        marks={[{ value: 5 }, { value: 20 }, { value: 35 }, { value: 50 }]}
                    />
                </Box>
            </Stack>
        </motion.div>
    );
});

export default IdentityStep;
